import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Mail, MapPin } from 'lucide-react';

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();

  const handleApplyClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      const element = document.getElementById('apply');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/#apply');
    }
  };

  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl font-['Outfit']">Q</span>
              </div>
              <span className="text-xl font-bold font-['Outfit']">QuickFinance</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Fast, flexible business funding from £10,000 to £10 million. Empowering UK businesses to grow with speed and confidence.
            </p>
            <div className="flex flex-col gap-3 text-sm text-slate-400">
              <a href="mailto:hello@quickfinance.io" className="flex items-center gap-2 hover:text-white transition-colors">
                <Mail size={16} />
                hello@quickfinance.io
              </a>
              <div className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                <span>71-75 Shelton Street<br />Covent Garden, London<br />WC2H 9JQ, United Kingdom</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-6 font-['Outfit']">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/" className="text-slate-400 hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/about" className="text-slate-400 hover:text-white transition-colors">About Us</Link></li>
              <li><Link to="/faq" className="text-slate-400 hover:text-white transition-colors">FAQ</Link></li>
              <li><Link to="/contact" className="text-slate-400 hover:text-white transition-colors">Contact</Link></li>
              <li><button onClick={handleApplyClick} className="text-slate-400 hover:text-white transition-colors">Apply Now</button></li>
            </ul>
          </div>

          {/* Loan Types */}
          <div>
            <h4 className="font-semibold text-lg mb-6 font-['Outfit']">Funding Options</h4>
            <ul className="space-y-3 text-sm">
              <li><span className="text-slate-400">Unsecured Business Loans</span></li>
              <li><span className="text-slate-400">Secured Business Loans</span></li>
              <li><span className="text-slate-400">Working Capital</span></li>
              <li><span className="text-slate-400">Equipment Finance</span></li>
              <li><span className="text-slate-400">VAT Loans</span></li>
              <li><span className="text-slate-400">Expansion Funding</span></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-lg mb-6 font-['Outfit']">Legal</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/privacy-policy" className="text-slate-400 hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-slate-400 hover:text-white transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/cookies" className="text-slate-400 hover:text-white transition-colors">Cookie Policy</Link></li>
              <li><Link to="/complaints" className="text-slate-400 hover:text-white transition-colors">Complaints Procedure</Link></li>
            </ul>
          </div>
        </div>

        {/* FCA Disclaimer */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="bg-slate-800/50 rounded-xl p-6 mb-8">
            <p className="text-xs text-slate-400 leading-relaxed">
              <strong className="text-slate-300">Important Information:</strong> QuickFinance.io is a trading style of Connexis Group Ltd. QuickFinance.io is a credit broker, not a lender. We may receive a commission from our funding partner for successful introductions. All finance is subject to eligibility, status, and approval by our funding partner. Terms and conditions apply. Decisions are made at the sole discretion of our funding partner based on their lending criteria. Interest rates and fees are determined by our funding partner and will be clearly disclosed before you accept any offer.
            </p>
            <p className="text-xs text-slate-400 mt-4">
              <strong className="text-slate-300">Representative Example:</strong> Amount of credit £10,000, duration 36 months, interest rate 9.9% per annum (fixed), representative APR 9.9%, total amount repayable £11,563, monthly repayment £321.
            </p>
          </div>

          <div className="flex flex-col items-center gap-3 text-sm text-slate-500">
            <p>© {new Date().getFullYear()} QuickFinance.io. All rights reserved.</p>
            <p className="text-xs text-slate-500 text-center leading-relaxed">
              QuickFinance.io is a trading style of Connexis Group Ltd. Registered in England & Wales. Company No. 17214446. ICO Registration No. ZC148599.
            </p>
            <p className="text-xs text-slate-500 text-center">
              Registered Address: 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 text-center">
              <p>Authorised and regulated by the Financial Conduct Authority</p>
              <Link 
                to="/login" 
                className="text-slate-500 hover:text-white transition-colors underline underline-offset-2 whitespace-nowrap" 
                data-testid="admin-login-footer"
              >
                Admin Portal
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
